import { getEnvVar } from '../utils/env-var.utils';

export interface ApiConfig {
  readonly origin: string;
  readonly originCheckout: string;
}

export interface CognitoConfig {
  readonly clientId: string;
  readonly userPoolId: string;
  readonly gandalfIdpName: string;
  readonly awsSkillBuilderIdpName: string;
  readonly amazonFederateIdpName: string;
  readonly appWebDomain: string;
  readonly gandalfLogin: string;
  readonly gandalfLogout: string;
  readonly gandalfRegion: string;
}

export interface AuthGroupNames {
  readonly generalAccess: string;
  readonly eventAdmin: string;
  readonly challengeAdmin: string;
  readonly campaignAdmin: string;
}

// billing info config interfaceß
export interface BillingConfig {
  readonly purchaseHistory: string;
}

export interface RumConfig {
  readonly guestRoleArn: string;
  readonly identityPoolId: string;
  readonly applicationId: string;
  readonly applicationRegion: string;
}

export interface WafConfig {
  readonly scriptUrl: string;
  readonly apiKey: string;
}

export interface FeatureFlags {
  enableVibeIdUser: boolean;
  enableVirtualMc: boolean;
  enablePARReport: boolean;
  enableAIAssistant: boolean;
  enableAsyncChallengeRecommendations: boolean;
  enableVariableScoring: boolean;
  disableGameUIAwsConsoleForChallenges?: string[];
  virtualMcVideoSourcesUrl?: string;
  enableGlobalNotifications: boolean;
  enableFacilitatorTools: boolean;
  enableChallengeValidation: boolean;
  enableAiTeammate: boolean;
  enableS3HostedUserGuide: boolean;
  enableReplaceChallengeWithBackup: boolean;
  enableArena: boolean;
  enableAwsServiceDetection: boolean;
}

export interface AppConfig {
  readonly api: ApiConfig;
  readonly cognito: CognitoConfig;
  readonly warningBannerMessage: string;
  readonly stageName: string;
  readonly isChallengeReviewEnabled: boolean;
  readonly isLrsIntegrationEnabled: boolean;
  readonly isGenAiIntegrationEnabled: boolean;
  readonly billing: BillingConfig;
  readonly rum: RumConfig;
  readonly waf: WafConfig;
  readonly enableACIEvents: boolean;
  readonly classroomAgreementId: string;
  readonly s3: { challengeResourcesBucketName: string; publicDocsBucketName: string };
  readonly ff: FeatureFlags;
  readonly enableGenAIEventCreate: boolean;
}

const localBackendOrigin = 'http://localhost:8040';

/**
 * Create a new app config by inspecting the environment.
 */
export const newAppConfig = (): AppConfig => {
  const useLocalBackend = process.env.REACT_APP_LOCAL_BACKEND === 'true';
  return {
    api: {
      origin: useLocalBackend ? /* istanbul ignore next */ localBackendOrigin : getEnvVar('REACT_APP_API_ORIGIN'),
      originCheckout: useLocalBackend
        ? /* istanbul ignore next */ localBackendOrigin
        : getEnvVar('REACT_APP_CHECKOUT_GRAPHQL_API_ORIGIN'),
    },
    cognito: {
      clientId: getEnvVar('REACT_APP_GANDALF_COGNITO_CLIENT_ID'),
      userPoolId: getEnvVar('REACT_APP_GANDALF_COGNITO_USER_POOL_ID'),
      gandalfIdpName: getEnvVar('REACT_APP_COGNITO_GANDALF_IDP_NAME'),
      amazonFederateIdpName: getEnvVar('REACT_APP_COGNITO_AMAZON_FEDERATE_IDP_NAME'),
      awsSkillBuilderIdpName: getEnvVar('REACT_APP_COGNITO_AWS_SKILL_BUILDER_NAME'),
      appWebDomain: getEnvVar('REACT_APP_GANDALF_COGNITO_APP_WEB_DOMAIN'),
      gandalfLogin: getEnvVar('REACT_APP_GANDALF_LOGIN_WEB_DOMAIN'),
      gandalfLogout: getEnvVar('REACT_APP_GANDALF_WEB_DOMAIN'),
      gandalfRegion: getEnvVar('REACT_APP_GANDALF_REGION'),
    },
    warningBannerMessage: getEnvVar('REACT_APP_WARNING_BANNER_MESSAGE', ''),
    stageName: getEnvVar('REACT_APP_STAGE_NAME'),
    isChallengeReviewEnabled: getEnvVar('REACT_APP_IS_CHALLENGE_REVIEW_ENABLED') === 'true',
    isLrsIntegrationEnabled: getEnvVar('REACT_APP_LRS_INTEGRATION_ENABLED') === 'true',
    isGenAiIntegrationEnabled: getEnvVar('REACT_APP_GEN_AI_INTEGRATION_ENABLED') === 'true',
    billing: {
      purchaseHistory: getEnvVar('REACT_APP_BILLING_PURCHASE_HISTORY_URL'),
    },
    rum: {
      guestRoleArn: getEnvVar('REACT_APP_RUM_GUEST_ROLE_ARN'),
      identityPoolId: getEnvVar('REACT_APP_RUM_IDENTITY_POOL_ID'),
      applicationId: getEnvVar('REACT_APP_RUM_APPLICATION_ID'),
      applicationRegion: getEnvVar('REACT_APP_RUM_APPLICATION_REGION'),
    },
    waf: {
      scriptUrl: getEnvVar('REACT_APP_WAF_SCRIPT_URL'),
      apiKey: getEnvVar('REACT_APP_WAF_API_KEY'),
    },
    enableACIEvents: getEnvVar('REACT_APP_ENABLE_ACI_EVENTS') === 'true',
    classroomAgreementId: getEnvVar('REACT_APP_CLASSROOM_AGREEMENT_ID'),
    enableGenAIEventCreate: getEnvVar('REACT_APP_ENABLE_GENAI_EVENT_CREATE') === 'true',
    s3: {
      challengeResourcesBucketName: getEnvVar('REACT_APP_CHALLENGE_RESOURCES_BUCKET_NAME'),
      publicDocsBucketName: getEnvVar('REACT_APP_PUBLIC_DOCS_BUCKET_NAME'),
    },
    ff: {
      enableVibeIdUser: getEnvVar('REACT_APP_ENABLE_VIBE_ID_USER') === 'true',
      enableVirtualMc: getEnvVar('REACT_APP_ENABLE_VIRTUAL_MC') === 'true',
      enablePARReport: getEnvVar('REACT_APP_ENABLE_PAR_REPORT', 'false') === 'true',
      enableAIAssistant: getEnvVar('REACT_APP_ENABLE_AI_ASSISTANT', 'false') === 'true',
      disableGameUIAwsConsoleForChallenges: (getEnvVar('REACT_APP_REINVENT2025_WS_CHALLENGES', '') || '')
        .split(',')
        .filter((e) => !!e),
      virtualMcVideoSourcesUrl: getEnvVar('REACT_APP_REINVENT2025_VIRTUALMC_VIDEO_SOURCES_URL', ''),
      enableAsyncChallengeRecommendations:
        getEnvVar('REACT_APP_ENABLE_ASYNC_CHALLENGE_RECOMMENDATIONS', 'false') === 'true',
      enableVariableScoring: getEnvVar('REACT_APP_ENABLE_VARIABLE_SCORING', 'false') === 'true',
      enableGlobalNotifications: getEnvVar('REACT_APP_GLOBAL_NOTIFICATIONS_ENABLED') === 'true',
      enableFacilitatorTools: getEnvVar('REACT_APP_ENABLE_FACILITATOR_TOOLS', 'false') === 'true',
      enableChallengeValidation: getEnvVar('REACT_APP_ENABLE_CHALLENGE_VALIDATION', 'false') === 'true',
      enableAiTeammate: getEnvVar('REACT_APP_ENABLE_AI_TEAMMATE', 'false') === 'true',
      enableS3HostedUserGuide: getEnvVar('REACT_APP_ENABLE_S3_HOSTED_USER_GUIDE', 'false') === 'true',
      enableReplaceChallengeWithBackup: getEnvVar('REACT_APP_ENABLE_REPLACE_CHALLENGE_WITH_BACKUP', 'false') === 'true',
      enableArena: getEnvVar('REACT_APP_ENABLE_ARENA', 'false') === 'true',
      enableAwsServiceDetection: getEnvVar('REACT_APP_ENABLE_AWS_SERVICE_DETECTION', 'false') === 'true',
    },
  };
};
